import { lessonsFor, mc, tf } from "./helpers";

const graphArticle = { label: "Graph Engineering Explained", href: "/blog/graph-engineering-explained" };
const academy = { label: "Thinking Academy", href: "/thinking-academy" };
const chat = { label: "Chat with GenieBot", href: "/chat" };

export const AI_FLUENCY_LESSONS = lessonsFor("ai-fluency", [
  {
    slug: "what-is-llm",
    title: "What Is a Large Language Model?",
    minutes: 5,
    learn: ["What an LLM does in plain terms", "Why it's good with language"],
    explanation: [
      "A large language model is trained on huge amounts of text to predict what words come next. That makes it good at writing, summarizing and explaining.",
      "It doesn't \"know\" things the way a database does — it generates likely text.",
    ],
    example: "When you ask for a birthday message, the model produces text that fits patterns it learned from many examples.",
    check: tf("An LLM looks up every answer in a fact database.", false, "It generates likely text, which is why checking matters."),
    takeaway: "LLMs predict text — powerful, but not a source of truth.",
    tool: chat,
    resource: academy,
  },
  {
    slug: "model-vs-assistant",
    title: "AI Model vs AI Assistant",
    minutes: 4,
    learn: ["The engine vs the product", "What assistants add on top"],
    explanation: ["The model is the engine. An assistant is a product built on a model, adding a chat interface, instructions, memory or safety features."],
    example: "GenieBot Chat is an assistant; the language model underneath is the engine.",
    interactive: "classify-ai",
    check: mc("single", "Which is an AI assistant?", ["*A chat app that answers questions using a model", "The raw model file", "A spreadsheet formula"], "Assistants wrap a model in a usable product."),
    takeaway: "Model = engine; assistant = the product you talk to.",
    tool: chat,
    resource: graphArticle,
  },
  {
    slug: "assistant-vs-agent",
    title: "AI Assistant vs AI Agent",
    minutes: 5,
    learn: ["What makes something an agent", "Why agents need guardrails"],
    explanation: [
      "An assistant mainly responds to you. An agent can plan steps and take actions with tools — like searching, booking or sending.",
      "Because agents act, they need limits and approval points.",
    ],
    example: "An agent checks your calendar, finds a free slot and schedules a meeting after you approve.",
    check: mc("single", "What's the key difference of an agent?", ["It has a nicer interface", "*It can take actions using tools", "It never makes mistakes"], "Taking actions is what makes it an agent."),
    takeaway: "Agents act — so keep approvals in place.",
    tool: { label: "Graph Engineering demo", href: "/tools/graph-demo" },
    resource: graphArticle,
  },
  {
    slug: "what-is-context",
    title: "What Is Context?",
    minutes: 4,
    learn: ["What the AI can 'see' at once", "Why long chats can lose detail"],
    explanation: ["Context is everything the AI is working with right now — your messages, pasted text and instructions. It has a size limit, so very long chats can lose earlier details."],
    example: "After a long conversation, the AI forgets a rule you gave at the start.",
    check: tf("AI can always remember everything from a very long chat.", false, "Context windows have limits; important details may drop off."),
    takeaway: "Context is the AI's working memory — keep it focused.",
    tool: chat,
    resource: { label: "Prompt Training", href: "/prompt-training" },
  },
  {
    slug: "what-are-tools",
    title: "What Are Tools?",
    minutes: 4,
    learn: ["How AI uses tools", "Examples of common tools"],
    explanation: ["Tools let AI do things beyond writing text — search the web, run a calculation, read a file or call another app."],
    example: "An assistant uses a web-search tool to find today's weather before answering.",
    check: mc("single", "Which is an example of an AI tool?", ["*Web search", "A longer prompt", "A friendly tone"], "Tools are capabilities the AI can call to act or fetch information."),
    takeaway: "Tools give AI hands; they also need limits.",
    tool: { label: "All tools", href: "/tools" },
    resource: graphArticle,
  },
  {
    slug: "what-is-rag",
    title: "What Is Retrieval / RAG?",
    minutes: 5,
    difficulty: "Intermediate",
    learn: ["How AI answers from your documents", "Why retrieval reduces guessing"],
    explanation: [
      "Retrieval-augmented generation (RAG) means the AI first looks up relevant passages from documents, then writes an answer based on them.",
      "It helps ground answers in real sources, though checking is still wise.",
    ],
    example: "Answering questions from a company handbook PDF by pulling the relevant section first.",
    check: tf("RAG helps AI base answers on specific documents.", true, "Retrieval supplies relevant text for the model to use."),
    takeaway: "RAG = look it up first, then answer.",
    tool: chat,
    resource: { label: "Learning Articles", href: "/blogs" },
  },
  {
    slug: "what-is-workflow",
    title: "What Is an AI Workflow?",
    minutes: 4,
    learn: ["Fixed steps vs open-ended agents", "Where workflows shine"],
    explanation: ["A workflow is a set sequence of steps — some AI, some software, some human approval — that runs the same way each time."],
    example: "Every Monday: collect sales numbers → AI writes a summary → manager approves → email the team.",
    check: mc("single", "What best describes a workflow?", ["*A repeatable sequence of defined steps", "A single chat message", "A random set of actions"], "Workflows are predictable and repeatable."),
    takeaway: "Workflows are predictable; use them for repeatable processes.",
    tool: { label: "Graph Engineering demo", href: "/tools/graph-demo" },
    resource: graphArticle,
  },
  {
    slug: "why-hallucinate",
    title: "Why AI Hallucinates",
    minutes: 4,
    learn: ["The cause behind made-up answers", "How to reduce them"],
    explanation: [
      "Because models predict likely text, they can fill gaps with plausible-sounding details.",
      "Giving sources, asking it to say \"I don't know\", and checking key facts all help.",
    ],
    example: "Asked about an obscure local law, the AI invents a section number.",
    check: mc("multi", "What reduces hallucinations? (Choose all that apply)", ["*Providing source documents", "*Allowing the AI to say it doesn't know", "Asking it to sound more confident", "*Checking key facts yourself"], "Grounding, honesty and verification help; confidence doesn't."),
    takeaway: "Ground it, allow \"I don't know\", and check.",
    tool: chat,
    resource: { label: "Hallucinations lesson", href: "/learn/ai-safety/hallucinations-explained" },
  },
  {
    slug: "when-automate",
    title: "When Automation Makes Sense",
    minutes: 4,
    learn: ["Good candidates for automation", "When simple rules beat AI"],
    explanation: [
      "Automate tasks that are frequent, repetitive and low-risk. If a fixed rule does the job reliably, use the rule — not AI.",
    ],
    example: "Sorting receipts into folders by date is a rule; summarizing customer feedback themes is a good AI task.",
    check: mc("scenario", "You need to add tax to every invoice total. Best approach?", ["*A simple formula or rule", "An AI agent that decides each time", "Ask a chatbot for every invoice"], "Deterministic tasks belong to deterministic rules."),
    takeaway: "Use rules for rules, AI for judgment-light language tasks.",
    tool: { label: "All tools", href: "/tools" },
    resource: graphArticle,
  },
  {
    slug: "human-in-control",
    title: "When a Human Should Stay in Control",
    minutes: 4,
    learn: ["High-stakes decisions", "Designing approval points"],
    explanation: ["Keep people in charge where mistakes are costly or affect others: money, health, legal matters, hiring and safety."],
    example: "AI drafts a refund decision, but a person approves anything over $500.",
    check: tf("AI should make final hiring decisions on its own.", false, "Decisions about people should stay with people."),
    takeaway: "The higher the stakes, the more a human decides.",
    tool: { label: "Graph Engineering demo", href: "/tools/graph-demo" },
    resource: graphArticle,
  },
]);
